import type { StudyMissionSelectDto, StudySwimlaneSelectDto, StudyThreadSelectDto } from '@@types/api.types';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { type FC, useMemo } from 'react';
import { meiaRoute } from '../meia.constants';
import { MeiaPhaseAdvanceBar } from './meia-phase-advance-bar.component';
import { getMeiaMissionPhaseProgress } from './meia-phase-progress.util';

type MeiaP2AdvanceBarProps = {
  mission: StudyMissionSelectDto;
  studyId: string;
  swimlanes: StudySwimlaneSelectDto[];
  taskRows: Array<{ swimlaneId: string; threadId: string }>;
  threads: StudyThreadSelectDto[];
};

export const MeiaP2AdvanceBar: FC<MeiaP2AdvanceBarProps> = (props) => {
  const { mission, studyId, swimlanes, taskRows, threads } = props;

  const progress = useMemo(
    () =>
      getMeiaMissionPhaseProgress('p2', {
        isStudySignedOff: false,
        measureFlagsByMissionId: new Map(),
        mission,
        stages: swimlanes.map((swimlane) => ({ id: swimlane.id, threadId: swimlane.threadId })),
        taskRows,
        threadScoresByThreadId: new Map(),
        threads: threads.map((thread) => ({ id: thread.id, missionId: thread.missionId })),
      }),
    [mission, swimlanes, taskRows, threads],
  );

  const coveredStageCount = useMemo(() => {
    const coveredStageIds = new Set(taskRows.map((row) => row.swimlaneId));
    return swimlanes.filter((swimlane) => coveredStageIds.has(swimlane.id)).length;
  }, [swimlanes, taskRows]);

  return (
    <MeiaPhaseAdvanceBar
      backLink={{
        children: (
          <>
            <ArrowLeftIcon className="size-4" /> Back to Phase 1
          </>
        ),
        to: meiaRoute.studyTab({ query: { missionId: mission.id }, studyId, tab: 'p1' }),
      }}
      caption={
        <>
          {coveredStageCount} of {swimlanes.length} stages covered by tasks · Gate G2 checks stage structure per thread
        </>
      }
      forwardLink={{
        children: (
          <>
            Advance to Phase 3 <ArrowRightIcon className="size-4" />
          </>
        ),
        to: meiaRoute.studyTab({ query: { missionId: mission.id }, studyId, tab: 'p3' }),
      }}
      title={<>Phase 2 Progress: {progress}%</>}
    />
  );
};
